import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Filter, X, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MultiSelect from "@/components/MultiSelect";
import EmployeesPicker from "@/components/EmployeesPicker";
import { api } from "@/api/client";

const PRESETS = ["today", "yesterday", "week", "month", "last_month", "year", "custom"];
const SOURCES = ["GENDETAILED_PROD", "IC385DETAIL", "IC385P2DETAIL"];
const TX_TYPES = ["RF PICK", "RF STAGE", "RF TRNS", "RF RCPT", "RF.RCVR", "RF LOAD"];

export default function GlobalFilters({ filters, onChange, onReset }) {
  const { t } = useTranslation();
  const [depts, setDepts] = React.useState([]);
  const [emps, setEmps] = React.useState([]);
  // Track the IDs the previous /employees call exposed, so we can prune the
  // current `employee_ids` selection when it falls outside the new scope.
  const lastValidEmpIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  // Fetch departments once.
  useEffect(() => {
    let alive = true;
    api
      .get("/departments")
      .then((r) => alive && setDepts(r.data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Re-fetch employees whenever department_ids or source_report changes.
  // Phase 4 Fix B — the multi-select must respect the active filter scope.
  // Phase 4.9b — the picker itself filters to distribution scope; here we
  // load the FULL list (in_scope_only=false) only to:
  //   1. show the "scoped: N" badge (count of in-distribution emps in this
  //      dept/source slice)
  //   2. prune `employee_ids` selections that no longer exist at all in the
  //      current dept/source. We must NOT prune ids that are just outside the
  //      distribution scope — the picker handles those with a ⚠️ badge.
  const deptKey = useMemo(
    () => JSON.stringify(filters.department_ids || []),
    [filters.department_ids]
  );
  const sourceKey = filters.source_report || "";
  useEffect(() => {
    let alive = true;
    const params = { in_scope_only: false };
    if (filters.department_ids?.length) {
      params.department_ids = filters.department_ids.join(",");
    }
    if (filters.source_report && filters.source_report !== "GENDETAILED_PROD") {
      params.source_report = filters.source_report;
    } else if (filters.source_report) {
      params.source_report = filters.source_report;
    }
    api
      .get("/employees", { params })
      .then((r) => {
        if (!alive) return;
        const data = r.data || [];
        // Only count in-distribution emps for the badge.
        setEmps(data.filter((e) => e.in_distribution_scope));
        const validIds = new Set(data.map((e) => e.employee_id));
        // Prune current selection if any selected employee is now out of the
        // dept/source slice altogether (not just outside the distribution
        // scope — those keep their ⚠️ badge in the picker).
        const current = filters.employee_ids || [];
        const stillValid = current.filter((id) => validIds.has(id));
        if (stillValid.length !== current.length) {
          const dropped = current.length - stillValid.length;
          if (!isFirstLoad.current && dropped > 0) {
            toast.info(t("filters.empsAutoDeselected", { n: dropped }));
          }
          onChange({ ...filters, employee_ids: stillValid });
        }
        lastValidEmpIds.current = validIds;
        isFirstLoad.current = false;
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptKey, sourceKey]);

  const deptOptions = useMemo(
    () => depts.map((d) => ({ value: d.id, label: d.name })),
    [depts]
  );
  const typeOptions = useMemo(
    () => TX_TYPES.map((tt) => ({ value: tt, label: tt })),
    []
  );

  const f = filters;
  const update = (patch) => onChange({ ...f, ...patch });

  const activeCount =
    (f.preset !== "year" ? 1 : 0) +
    (f.source_report && f.source_report !== "GENDETAILED_PROD" ? 1 : 0) +
    (f.transaction_types?.length || 0 ? 1 : 0) +
    (f.department_ids?.length || 0 ? 1 : 0) +
    (f.employee_ids?.length || 0 ? 1 : 0);

  const scoped =
    (f.department_ids?.length || 0) > 0 ||
    (f.source_report && f.source_report !== "GENDETAILED_PROD");

  return (
    <div
      data-testid="global-filters"
      className="sticky top-16 z-10 mb-6 rounded-xl border border-border/80 bg-card/90 p-4 shadow-sm backdrop-blur"
    >
      <div className="mb-3 flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{t("filters.title")}</span>
        {activeCount > 0 && (
          <Badge variant="secondary" className="text-[10px]" data-testid="filters-active-count">
            {activeCount}
          </Badge>
        )}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/help#dashboard"
                data-testid="filters-help-link"
                className="ml-1 text-muted-foreground hover:text-foreground"
                aria-label={t("filters.helpAria")}
              >
                <Info className="h-3.5 w-3.5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              {t("filters.helpTip")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          data-testid="filters-reset"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1 h-3.5 w-3.5" />
          {t("common.reset")}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {/* Preset */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t("filters.preset")}</Label>
          <Select
            value={f.preset}
            onValueChange={(v) => update({ preset: v })}
          >
            <SelectTrigger data-testid="filter-preset" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`filter-preset-${p}`}>
                  {t(`filters.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Custom date range */}
        {f.preset === "custom" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("filters.dateFrom")}</Label>
              <Input
                type="date"
                value={f.date_from || ""}
                onChange={(e) => update({ date_from: e.target.value || null })}
                data-testid="filter-date-from"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("filters.dateTo")}</Label>
              <Input
                type="date"
                value={f.date_to || ""}
                onChange={(e) => update({ date_to: e.target.value || null })}
                data-testid="filter-date-to"
                className="h-9"
              />
            </div>
          </>
        )}

        {/* Source report */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t("filters.sourceReport")}</Label>
          <Select
            value={f.source_report || "GENDETAILED_PROD"}
            onValueChange={(v) => update({ source_report: v })}
          >
            <SelectTrigger data-testid="filter-source" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`filter-source-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Transaction types */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t("filters.transactionTypes")}</Label>
          <MultiSelect
            options={typeOptions}
            value={f.transaction_types || []}
            onChange={(v) => update({ transaction_types: v })}
            placeholder={t("filters.all")}
            testId="filter-types"
          />
        </div>

        {/* Departments */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t("filters.departments")}</Label>
          <MultiSelect
            options={deptOptions}
            value={f.department_ids || []}
            onChange={(v) => update({ department_ids: v })}
            placeholder={t("filters.all")}
            testId="filter-depts"
          />
        </div>

        {/* Employees */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">{t("filters.employees")}</Label>
            {scoped && (
              <Badge
                variant="outline"
                className="text-[9px] uppercase tracking-wider"
                data-testid="filter-emps-scoped"
              >
                {t("filters.scoped", { n: emps.length })}
              </Badge>
            )}
          </div>
          <EmployeesPicker
            value={f.employee_ids || []}
            onChange={(v) => update({ employee_ids: v })}
            sourceReport={f.source_report || undefined}
            departmentIds={f.department_ids || []}
            placeholder={t("filters.all")}
            testId="filter-emps"
          />
        </div>
      </div>
    </div>
  );
}
